/**
 * OJAL BEAUTY — GSAP Stacking Cards Interactive Controller
 * Features smooth scroll-driven card stacking via GSAP ScrollTrigger,
 * plus interactive click-to-cycle support.
 */

(function () {
  'use strict';

  function initStackCards() {
    const section = document.querySelector('.stack-section');
    if (!section) return;

    const deck = section.querySelector('.stack-deck');
    const cards = Array.from(section.querySelectorAll('.stack-card'));
    if (!cards.length) return;

    // Check if GSAP and ScrollTrigger are available
    if (typeof gsap !== 'undefined') {
      if (typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
      }

      initGsapScroll(section, cards);
    } else {
      // Fallback click-to-cycle if GSAP is not yet loaded
      initClickCycle(cards);
    }

    // Scroll to top button
    const scrollBtn = section.querySelector('.stack-scroll-indicator');
    if (scrollBtn) {
      scrollBtn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  function initGsapScroll(section, cards) {
    if (typeof ScrollTrigger === 'undefined') {
      initClickCycle(cards);
      return;
    }

    const deck = section.querySelector('.stack-deck');
    const totalCards = cards.length;

    // Set initial card states for Bottom-to-Top Stacking:
    // Card 0 starts front & center.
    // Subsequent cards start below (yPercent: 125) with higher zIndex so they slide UP on top of previous cards.
    cards.forEach((card, i) => {
      if (i === 0) {
        gsap.set(card, {
          x: 0,
          y: 0,
          yPercent: 0,
          scale: 1,
          rotation: 0,
          opacity: 1,
          zIndex: 10,
        });
      } else {
        const startRot = i % 2 === 1 ? 3.5 : -3.5;
        gsap.set(card, {
          x: 0,
          y: 0,
          yPercent: 130,
          scale: 1,
          rotation: startRot,
          opacity: 1,
          zIndex: 10 + i * 10,
        });
      }
    });

    // Create a pinned timeline for smooth scroll-driven bottom-to-top stacking
    if (window.innerWidth > 768 && totalCards > 1) {
      const scrollDistance = (totalCards - 1) * 750;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          pin: true,
          start: 'top top',
          end: () => `+=${scrollDistance}`,
          scrub: 1.2, // Silky smooth inertia scrubbing
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      // As user scrolls:
      // Current card slides UP from the bottom directly on top of previous card at its position.
      // Previous card underneath scales down slightly and rotates into the background stack.
      for (let i = 1; i < totalCards; i++) {
        const prevCard = cards[i - 1];
        const currentCard = cards[i];
        const label = `step-${i}`;
        const stackRot = i % 2 === 1 ? -4 : 4;
        const stackY = i % 2 === 1 ? -4 : 6;

        // Underneath card scales down and rotates slightly into the background deck
        tl.to(
          prevCard,
          {
            scale: 0.94,
            rotation: stackRot,
            y: stackY,
            opacity: 0.92,
            duration: 1,
            ease: 'power1.inOut',
          },
          label
        );

        // New card comes from the BOTTOM and lands directly ON TOP into position
        tl.to(
          currentCard,
          {
            yPercent: 0,
            x: 0,
            y: 0,
            scale: 1,
            rotation: 0,
            opacity: 1,
            duration: 1,
            ease: 'power2.out',
          },
          label
        );
      }
    }

    // Interactive Click-to-Advance: Bottom to Top
    let activeIndex = 0;
    if (deck) {
      deck.addEventListener('click', () => {
        if (totalCards <= 1) return;

        const prevIndex = activeIndex;
        activeIndex = (activeIndex + 1) % totalCards;

        // If cycling back to 0, reset cards to bottom
        if (activeIndex === 0) {
          cards.forEach((card, idx) => {
            if (idx === 0) {
              gsap.to(card, {
                yPercent: 0,
                scale: 1,
                rotation: 0,
                opacity: 1,
                zIndex: 10,
                duration: 0.7,
                ease: 'power2.out',
              });
            } else {
              gsap.to(card, {
                yPercent: 130,
                scale: 1,
                rotation: idx % 2 === 1 ? 3.5 : -3.5,
                duration: 0.7,
                ease: 'power2.inOut',
              });
            }
          });
          return;
        }

        // Active card underneath scales down
        gsap.to(cards[prevIndex], {
          scale: 0.94,
          rotation: activeIndex % 2 === 1 ? -4 : 4,
          opacity: 0.92,
          duration: 0.65,
          ease: 'power1.inOut',
        });

        // New card comes from bottom onto top
        gsap.to(cards[activeIndex], {
          yPercent: 0,
          scale: 1,
          rotation: 0,
          opacity: 1,
          zIndex: 10 + activeIndex * 10,
          duration: 0.7,
          ease: 'power2.out',
        });
      });
    }
  }

  // Fallback click cycle
  function initClickCycle(cards) {
    if (cards.length <= 1) return;
    let current = 0;

    cards.forEach((card, idx) => {
      card.style.zIndex = String(10 + idx * 10);
      card.style.transform = idx === 0 ? 'translate3d(0, 0, 0)' : 'translate3d(0, 120%, 0)';
      card.style.transition = 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)';
    });

    const deck = document.querySelector('.stack-deck');
    if (deck) {
      deck.addEventListener('click', () => {
        current = (current + 1) % cards.length;
        cards.forEach((card, idx) => {
          if (idx <= current) {
            card.style.transform = 'translate3d(0, 0, 0)';
          } else {
            card.style.transform = 'translate3d(0, 120%, 0)';
          }
        });
      });
    }
  }

  // Interactive click-to-cycle functionality (also works on mobile)
  function initClickCycle(cards) {
    if (cards.length <= 1) return;

    let activeIndex = 0;
    const rotations = [-2.5, 2, -1.8, 2.2];

    function updateStack() {
      cards.forEach((card, idx) => {
        const offset = (idx - activeIndex + cards.length) % cards.length;

        if (offset === 0) {
          // Front active card
          card.style.zIndex = '10';
          card.style.transform = 'translate3d(0, 0, 0) scale(1) rotate(0deg)';
          card.style.opacity = '1';
        } else if (offset === 1) {
          // First card behind
          card.style.zIndex = '9';
          card.style.transform = `translate3d(0, 6px, 0) scale(0.95) rotate(${rotations[0]}deg)`;
          card.style.opacity = '0.9';
        } else {
          // Other cards behind
          card.style.zIndex = String(8 - offset);
          card.style.transform = `translate3d(0, 12px, 0) scale(0.90) rotate(${rotations[1]}deg)`;
          card.style.opacity = '0.7';
        }
        card.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease';
      });
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        activeIndex = (activeIndex + 1) % cards.length;
        updateStack();
      });
    });

    updateStack();
  }

  // DOM Loaded listener
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStackCards);
  } else {
    initStackCards();
  }

  // Shopify section event handler
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.stack-section')) {
      initStackCards();
    }
  });
})();
